import { useState } from "react";
import AdminHeader from "@/components/admin/AdminHeader";
import OptimizedUserManagement from "@/components/admin/OptimizedUserManagement";
import OptimizedUserCreditsPanel from "@/components/admin/credits/OptimizedUserCreditsPanel";
import UserHistoryPanel from "@/components/admin/UserHistoryPanel";
import CreditAvailabilityPanel from "@/components/admin/CreditAvailabilityPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

import WebhookTesting from "@/components/admin/WebhookTesting";

export default function Admin() {
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const handleRefresh = () => {
    setIsLoading(true);
    // Refresh logic can be implemented here
    setTimeout(() => setIsLoading(false), 1000);
  };

  const handleSyncStripe = () => {
    setIsSyncing(true);
    // Stripe sync logic can be implemented here
    setTimeout(() => setIsSyncing(false), 2000);
  };

  const handleCleanupDuplicates = async () => {
    setCleanupLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('cleanup-duplicate-subscriptions');

      if (error) {
        console.error("Function error:", error);
        toast.error(`Cleanup failed: ${error.message}`);
      } else {
        console.log("Function result:", data);
        toast.success("Duplicate subscriptions cleanup initiated!");
      }
    } catch (err: any) {
      console.error("Unexpected error:", err);
      toast.error(`Unexpected error: ${err.message}`);
    } finally {
      setCleanupLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader 
        onRefresh={handleRefresh}
        isLoading={isLoading}
        onSyncStripe={handleSyncStripe}
        isSyncing={isSyncing}
      />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Admin Dashboard</h1>
            <p className="text-muted-foreground">
              Manage users, monitor system health, and configure settings
            </p>
          </div>

          <Tabs defaultValue="users" className="w-full">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="credits">Credits</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
              <TabsTrigger value="status">Status</TabsTrigger>
              <TabsTrigger value="webhook">Webhook</TabsTrigger>
              <TabsTrigger value="cleanup">Cleanup</TabsTrigger>
            </TabsList>

            <TabsContent value="users" className="space-y-4">
              <OptimizedUserManagement />
            </TabsContent>

            <TabsContent value="credits" className="space-y-4">
              <OptimizedUserCreditsPanel />
            </TabsContent>

            <TabsContent value="history" className="space-y-4">
              <UserHistoryPanel />
            </TabsContent>

            <TabsContent value="status" className="space-y-4">
              <CreditAvailabilityPanel />
            </TabsContent>

            <TabsContent value="webhook" className="space-y-4">
              <WebhookTesting />
            </TabsContent>

            <TabsContent value="cleanup" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>System Cleanup</CardTitle>
                  <CardDescription>
                    Clean up duplicate subscriptions and fix data inconsistencies
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={handleCleanupDuplicates}
                    disabled={cleanupLoading}
                    variant="outline"
                    className="w-full"
                  >
                    {cleanupLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                    Clean Up Duplicate Subscriptions
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
